import React, { useState, useEffect, useMemo, createContext, SetStateAction, Dispatch, ReactNode, FunctionComponent } from 'react';
import { useContext } from 'react';

import { Player, DraftData, EmptyDraftData, DraftOptions, DefaultDraftOptions } from '../types'
import { Tournament, EmptyTournament } from '../../tour/types'

import { GetDefaultPlayers } from '../utils/playerUtils'

export interface Context {
    players: Player[];
    setPlayers: Dispatch<SetStateAction<Player[]>>;
    gameData: DraftData;
    setGameData: Dispatch<SetStateAction<DraftData>>;
    tournament: Tournament;
    setTournament: Dispatch<SetStateAction<Tournament>>;
    searchText: string;
    setSearchText: Dispatch<SetStateAction<string>>;
    draftOptions: DraftOptions;
    setDraftOptions: Dispatch<SetStateAction<DraftOptions>>;

}

const MeleeContext = createContext<Context | null>(null);

export const gameDataStorageName = 'local_gameData'
const optionsStorageKey = 'draft_options'

interface BaseLayoutProps {
    children?: ReactNode;
}

export const saveTour = (tournament: Tournament) => {
    localStorage.setItem(`${tournament.Id}`, JSON.stringify(tournament));
}

export const MeleeContextProvider: FunctionComponent<BaseLayoutProps> = ({ children }) => {
    const [players, setPlayers] = useState<Player[]>(GetDefaultPlayers());
    const [gameData, setDraftData] = useState<DraftData>(EmptyDraftData());
    const [tournament, setTournament] = useState<Tournament>(EmptyTournament());
    const [searchText, setSearchText] = useState<string>('');
    const [draftOptions, setDraftOptions] = useState<DraftOptions>(DefaultDraftOptions);

    useEffect(() => {
        try {
            let storedRound = localStorage.getItem(gameDataStorageName);
            if (storedRound !== '' && storedRound !== null && storedRound !== undefined) {
                var storedDraft: DraftData = JSON.parse(`${storedRound}`);
                if (storedDraft.Active)
                    setDraftData(storedDraft);
            }

            let storedOptions = localStorage.getItem(optionsStorageKey)
            if (storedOptions !== '' && storedOptions !== null && storedOptions !== undefined) {
                var storedOptionsData: DraftOptions = JSON.parse(`${storedOptions}`);
                if(isSameAsDefault(storedOptionsData)) return;
                setDraftOptions(storedOptionsData);
            }
        } catch (err) {
            setDraftData(EmptyDraftData);
            console.error("Could not fetch round data: ", err);
        }
    }, [])

    useEffect(() => {
        if(tournament === EmptyTournament()) return;
        localStorage.setItem(`${tournament.Id}`, JSON.stringify(tournament));
    }, [tournament])

    useEffect(() => {
        // We dont store current draft if empty
        if (Object.keys(gameData).length === 0) return;
        localStorage.setItem(gameDataStorageName, JSON.stringify(gameData));
    }, [gameData])


    useEffect(() => {
        if(isSameAsDefault(draftOptions)) return;
        localStorage.setItem(optionsStorageKey, JSON.stringify(draftOptions));
    }, [setDraftOptions, draftOptions])


    const value = useMemo(() => ({
        players,
        setPlayers,
        gameData,
        setGameData: setDraftData,
        tournament,
        setTournament,
        searchText,
        setSearchText,
        draftOptions,
        setDraftOptions,
    }), [players, setPlayers, gameData, setDraftData, tournament,
        setTournament, searchText, setSearchText, draftOptions, setDraftOptions]);

    return (
        <MeleeContext.Provider value={value}>
            {children}
        </MeleeContext.Provider>
    )
};


export const useMeleeContext = () => {
    const context = useContext(MeleeContext);
    return context;
}

const isSameAsDefault = (data : DraftOptions) => {
 return JSON.stringify(data) === JSON.stringify(DefaultDraftOptions())
}